import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { concatMap } from 'rxjs/operators';
import { PopupDialog, DialogResult } from '@app/Common/popup/popup.dialog';
import { ToastButtons, ToastMessageType, ToastResponse, ToastService } from '@app/Common/toast';
import { Permissions } from '@app/Models/Permissions';
import { TenantService } from '@app/Services';
import { PermissionService } from '@app/Services/permission.service';
import { TranslationPipe } from '@app/Services/translation.pipe';
import { Module, ModuleCard } from '../../Models';
import { ModuleService } from '../../Services/module.service';
import { PackageService } from '../../Services/package.service';
import { TranslationService } from '../../Services/translation.service';
import { ModuleCardComponent } from '../module-card.component/module-card.component';
import { MatMenuItem } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';

@Component({
    selector: 'aw-modules',
    templateUrl: './modules.component.html',
    styleUrls: ['./modules.component.less'],
    imports: [ModuleCardComponent, RouterLink, MatMenuItem, MatIcon, MatButton]
})
export class ModulesComponent implements OnInit {
    loading: boolean = true;
    moduleCards: ModuleCard[] = [];
    readonly permissions = Permissions;

    constructor(
        private activatedRoute: ActivatedRoute,
        public moduleService: ModuleService,
        private tenantService: TenantService,
        private packageService: PackageService,
        private router: Router,
        public permissionService: PermissionService,
        public toast: ToastService,
        private translationService: TranslationService,
        public dialog: MatDialog,
    ) {

    }

    async ngOnInit() {
        await this.getModules();
    }

    async getModules(): Promise<any> {
        this.loading = true;
        await this.moduleService.getModuleCards(this.tenantService.selectedTenant)
            .then(moduleCards => {
                this.moduleCards = moduleCards;
                this.loading = false;
            });
    }

    async createPackage(moduleCard: ModuleCard) {
        let createNewMajorVersion: boolean;

        const packageCreationRules = await this.packageService.getPackageCreationRules(this.tenantService.selectedTenant, moduleCard.ModuleId);
        if (packageCreationRules.CanCreateMajorPackage && packageCreationRules.CanCreateMinorPackage) {
            const dialogReference = this.dialog.open<PopupDialog, {}, DialogResult>(PopupDialog);
            const dialogInstance = dialogReference.componentInstance;
            dialogInstance.DisplayOptionClear = false;
            dialogInstance.DialogTitle = 'Package Type';
            dialogInstance.DialogConfirmButtonText = 'Create';
            dialogInstance.DialogCancelButtonText = 'Cancel';
            dialogInstance.DialogSelectedOption = 'Major Version';
            dialogInstance.DialogOptions = ['Major Version', 'Minor Version'];
            const packageType = await dialogReference.afterClosed().toPromise();
            if (!packageType?.Result) {
                return;
            }
            createNewMajorVersion = packageType.Text === 'Major Version';
        }
        else if (packageCreationRules.CanCreateMajorPackage) {
            // Can only create a major version if the base version is the best practice
            createNewMajorVersion = true;
        }
        else {
            // Can only create a minor version if the base version is not the latest
            createNewMajorVersion = false;
        }

        const response = await this.packageService.createPackage(this.tenantService.selectedTenant, moduleCard.ModuleId, createNewMajorVersion);

        if (response.Success) {
            this.router.navigate(['../packages-from-create-package'], {
                relativeTo: this.activatedRoute.parent
            });
        } else {
            this.showCreatePackageErrors(moduleCard.ModuleName, response.Errors);
        }
    }

    private showCreatePackageErrors(moduleName: string, errors: string[]) {
        const dialogRef = this.dialog.open(PopupDialog);
        dialogRef.componentInstance.DialogTitle = `Module Errors - ${moduleName}`;

        const moduleErrorsHtml = errors.map(error => `<p>${error}</p>`).join('');

        dialogRef.componentInstance.DialogDescription = moduleErrorsHtml;
        dialogRef.componentInstance.DialogConfirmButtonText = 'Ok';
    }

    goToFormFlowPatchMigration(module: Module) {
        this.router.navigate(['../migration/', module.ModuleId], {
            relativeTo: this.activatedRoute.parent.parent
        });
    }

    resetModuleToLatest(moduleCard: ModuleCard) {
        new TranslationPipe(this.translationService).transform(moduleCard.ModuleName, 'Text').subscribe(moduleName => {
            const dialogRef = this.dialog.open(PopupDialog);
            dialogRef.componentInstance.DialogTitle = 'Reset Module - ' + moduleName;
            dialogRef.componentInstance.DialogDescription = 'Are you sure you want to reset this module and lose any unpublished changes?';
            dialogRef.componentInstance.DialogConfirmButtonText = 'Yes';
            dialogRef.componentInstance.DialogCancelButtonText = 'No';

            dialogRef.afterClosed().subscribe((res) => {
                if (!res.Result) {
                    return;
                }
                this.moduleService.resetModuleContent(
                    this.tenantService.selectedTenant,
                    moduleCard.ModuleId,
                    null)
                    .then(() => {
                        this.moduleService.getModuleCards(this.tenantService.selectedTenant)
                            .then(moduleCards => { this.moduleCards = moduleCards; });
                    });
            });
        });
    }

    disableModuleForTenant(module: Module) {
        if (!this.permissionService.hasPermission(Permissions.moduleAccessEdit)) {
            this.toast.sendMessage('Access Denied', 'You do not have access to this feature', ToastMessageType.info);
            return;
        }
        const tenant = this.tenantService.selectedTenant.Value;
        this.toast.sendMessage('Disable ' + module.ModuleId,
            'Are you sure you want to Disable ' + module.ModuleName + ' for ' + tenant,
            ToastMessageType.error,
            ToastButtons.YesNo,
            0)
            .subscribe((result) => {
                if (result === ToastResponse.Yes) {
                    this.tenantService.getAllowedModules(tenant).pipe(
                        concatMap((modules: string[]) => {
                            modules.splice(modules.indexOf(module.ModuleId), 1);
                            return this.tenantService.setAllowedModules(tenant, modules);
                        }),
                        concatMap(() => this.tenantService.resetCache(tenant)))
                        .subscribe(() =>
                            this.getModules()
                        );
                }
            });
    }

    enableModuleForTenant(module: Module) {
        if (!this.permissionService.hasPermission(Permissions.moduleAccessEdit)) {
            this.toast.sendMessage('Access Denied', 'You do not have access to this feature', ToastMessageType.info);
            return;
        }
        const tenant = this.tenantService.selectedTenant.Value;

        new TranslationPipe(this.translationService).transform(module.ModuleName, 'Text').subscribe(moduleName => {
            this.toast.sendMessage('Enable Module',
                'Are you sure you want to Enable ' + moduleName + ' for ' + this.tenantService.selectedTenant.Value,
                ToastMessageType.info,
                ToastButtons.YesNo,
                0)
                .subscribe((result) => {
                    if (result === ToastResponse.Yes) {
                        this.tenantService.getAllowedModules(tenant)
                            .pipe(
                                concatMap((modules: string[]) => {
                                    modules.push(module.ModuleId);
                                    return this.tenantService.setAllowedModules(tenant, modules);
                                }),
                                concatMap(() => this.tenantService.resetCache(tenant)))
                            .subscribe(() =>
                                this.getModules()
                            );
                    }
                });
        });
    }

    rebuildTranslations() {
        this.translationService.rebuildTranslationsForTenant(this.tenantService.selectedTenant.Value);
    }
}
